import React, { useState } from 'react';
import {
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  Divider,
  Box,
  IconButton,
  Typography,
  Chip,
  Collapse
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { Close as CloseIcon, ExpandMore, ChevronRight } from '@mui/icons-material';
import { GameType } from '../../types/GameType.ts';
import { getGameConfig, GameConfig } from '../../types/GameConfig.ts';
import { setNotificationSeen } from '../../utils/userStatsUtils.ts';
import { getIconComponent } from '../../utils/iconUtils.ts';
import { logRouteToGame } from '../../utils/loggingUtils.ts';
import { useSidebar } from '../GameContainer/SidebarContext.tsx';
import { useNotification } from '../GameContainer/NotificationContext.tsx';
import { useAuth } from '../../supabase/AuthContext.tsx';

// Helper function to create hover color from primary color
const createHoverColor = (primaryColor: string): string => {
  const hex = primaryColor.replace('#', '');
  const r = parseInt(hex.substr(0, 2), 16);
  const g = parseInt(hex.substr(2, 2), 16);
  const b = parseInt(hex.substr(4, 2), 16);
  const lightR = Math.round(r * 0.1 + 255 * 0.9);
  const lightG = Math.round(g * 0.1 + 255 * 0.9);
  const lightB = Math.round(b * 0.1 + 255 * 0.9);
  return `rgb(${lightR}, ${lightG}, ${lightB})`;
};

// Reddit icon component
const RedditIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0zm5.01 4.744c.688 0 1.25.561 1.25 1.249a1.25 1.25 0 0 1-2.498.056l-2.597-.547-.8 3.747c1.824.07 3.48.632 4.674 1.488.308-.309.73-.491 1.207-.491.968 0 1.754.786 1.754 1.754 0 .716-.435 1.333-1.01 1.614a3.111 3.111 0 0 1 .042.52c0 2.694-3.13 4.87-7.004 4.87-3.874 0-7.004-2.176-7.004-4.87 0-.183.015-.366.043-.534A1.748 1.748 0 0 1 4.028 12c0-.968.786-1.754 1.754-1.754.463 0 .898.196 1.207.49 1.207-.883 2.878-1.43 4.744-1.487l.885-4.182a.342.342 0 0 1 .14-.197.35.35 0 0 1 .238-.042l2.906.617a1.214 1.214 0 0 1 1.108-.701zM9.25 12C8.561 12 8 12.562 8 13.25c0 .687.561 1.248 1.25 1.248.687 0 1.248-.561 1.248-1.249 0-.688-.561-1.249-1.249-1.249zm5.5 0c-.687 0-1.248.561-1.248 1.25 0 .687.561 1.248 1.249 1.248.688 0 1.249-.561 1.249-1.249 0-.687-.562-1.249-1.25-1.249zm-5.466 3.99a.327.327 0 0 0-.231.094.33.33 0 0 0 0 .463c.842.842 2.484.913 2.961.913.477 0 2.105-.056 2.961-.913a.361.361 0 0 0 .029-.463.33.33 0 0 0-.464 0c-.547.533-1.684.73-2.512.73-.828 0-1.979-.196-2.512-.73a.326.326 0 0 0-.232-.095z"/>
  </svg>
);

interface SidebarGameItemProps {
  gameConfig: GameConfig;
  hasLaunched: boolean;
  showNotification: boolean;
  showArchiveNotification?: boolean;
  onNavigate: () => void;
  onArchiveClick: () => void;
  onArchiveExpand?: () => void;
}

function SidebarGameItem({ gameConfig, hasLaunched, showNotification, showArchiveNotification, onNavigate, onArchiveClick, onArchiveExpand }: SidebarGameItemProps) {
  const [expanded, setExpanded] = useState(false);
  const IconComp = getIconComponent(gameConfig.icon);

  return (
    <>
      <ListItemButton
        onClick={() => {
          if (hasLaunched) {
            onNavigate();
          }
        }}
        disabled={!hasLaunched}
        sx={{
          transition: 'all 0.2s',
          opacity: hasLaunched ? 1 : 0.6,
          '&:hover': hasLaunched ? {
            backgroundColor: createHoverColor(gameConfig.primaryColor),
            transform: 'translateX(4px)'
          } : {},
          '& .MuiListItemIcon-root': {
            minWidth: '40px'
          }
        }}
      >
        <ListItemIcon>
          {IconComp && <IconComp style={{ width: 36, height: 36 }} />}
        </ListItemIcon>
        <ListItemText
          primary={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <span style={{ display: 'flex', alignItems: 'center' }}>
                {gameConfig.name}
                {showNotification && (
                  <span className="notification-dot" style={{ marginLeft: 6, position: 'static' }} />
                )}
              </span>
              {!hasLaunched && (
                <Chip
                  label="Coming Soon"
                  size="small"
                  sx={{
                    fontSize: '0.7rem',
                    height: '20px',
                    backgroundColor: '#f0f0f0',
                    color: '#666'
                  }}
                />
              )}
            </Box>
          }
        />
        {hasLaunched && (
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              const willExpand = !expanded;
              setExpanded(willExpand);
              if (willExpand && onArchiveExpand) {
                onArchiveExpand();
              }
            }}
            sx={{ p: 0.5, position: 'relative' }}
          >
            {expanded ? <ExpandMore fontSize="small" /> : <ChevronRight fontSize="small" />}
            {showArchiveNotification && !expanded && (
              <span className="notification-dot" style={{ width: 8, height: 8, top: 2, right: 2 }} />
            )}
          </IconButton>
        )}
      </ListItemButton>
      {hasLaunched && (
        <Collapse in={expanded} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            <ListItemButton
              sx={{ pl: 9, py: 0.5 }}
              onClick={onArchiveClick}
            >
              <ListItemText
                primary={`${gameConfig.name} Archives`}
                primaryTypographyProps={{ fontSize: '0.9rem' }}
              />
            </ListItemButton>
          </List>
        </Collapse>
      )}
    </>
  );
}

export default function Sidebar(): React.JSX.Element {
  const navigate = useNavigate();
  const { isSidebarOpen, setIsSidebarOpen } = useSidebar();
  const { showWeaverXLaunchNotification, setShowWeaverXLaunchNotification, showSubtextLaunchNotification, setShowSubtextLaunchNotification, showArchiveLaunchNotification, setShowArchiveLaunchNotification } = useNotification();
  const { user, signOut, openAuthModal } = useAuth();

  const weaverConfig = getGameConfig(GameType.WEAVER);
  const weaverXConfig = getGameConfig(GameType.WEAVER_X);
  const subtextConfig = getGameConfig(GameType.SUBTEXT);

  const weaverXHasLaunched = weaverXConfig.hasGameLaunched();
  const subtextHasLaunched = subtextConfig.hasGameLaunched();

  const getArchivePath = (config: GameConfig) => {
    const base = config.routePath.endsWith('/') ? config.routePath.slice(0, -1) : config.routePath;
    return `${base}/archive`;
  };

  return (
    <Drawer
      anchor="left"
      open={isSidebarOpen}
      onClose={() => setIsSidebarOpen(false)}
    >
      <Box sx={{ width: 300, height: '100%', display: 'flex', flexDirection: 'column' }}>
        {/* Header */}
        <Box sx={{
          p: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <Typography
            variant="h5"
            sx={{
              cursor: 'pointer',
              fontWeight: 'bold',
              '&:hover': {
                color: 'primary.main'
              }
            }}
            onClick={() => {
              navigate('/');
              setIsSidebarOpen(false);
            }}
          >
            Weaver Games
          </Typography>
          <IconButton
            onClick={() => setIsSidebarOpen(false)}
            size="small"
            sx={{
              p: 0.5,
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.04)'
              }
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
        <Divider />

        {/* Games Section */}
        <List>
          <ListItem>
            <Typography fontWeight="bold">
              Games
            </Typography>
          </ListItem>
          <SidebarGameItem
            gameConfig={weaverConfig}
            hasLaunched={true}
            showNotification={false}
            showArchiveNotification={showArchiveLaunchNotification}
            onNavigate={() => {
              logRouteToGame(GameType.WEAVER, 'sidebar');
              navigate(weaverConfig.routePath);
              setIsSidebarOpen(false);
            }}
            onArchiveClick={() => {
              navigate(getArchivePath(weaverConfig));
              setIsSidebarOpen(false);
            }}
            onArchiveExpand={() => {
              setNotificationSeen('archive-launch');
              setShowArchiveLaunchNotification(false);
            }}
          />
          <SidebarGameItem
            gameConfig={weaverXConfig}
            hasLaunched={weaverXHasLaunched}
            showNotification={showWeaverXLaunchNotification}
            onNavigate={() => {
              setNotificationSeen('weaver-x-launch');
              setShowWeaverXLaunchNotification(false);
              logRouteToGame(GameType.WEAVER_X, 'sidebar');
              navigate(weaverXConfig.routePath);
              setIsSidebarOpen(false);
            }}
            onArchiveClick={() => {
              navigate(getArchivePath(weaverXConfig));
              setIsSidebarOpen(false);
            }}
          />
          <SidebarGameItem
            gameConfig={subtextConfig}
            hasLaunched={subtextHasLaunched}
            showNotification={showSubtextLaunchNotification}
            onNavigate={() => {
              setNotificationSeen('subtext-launch');
              setShowSubtextLaunchNotification(false);
              logRouteToGame(GameType.SUBTEXT, 'sidebar');
              navigate(subtextConfig.routePath);
              setIsSidebarOpen(false);
            }}
            onArchiveClick={() => {
              navigate(getArchivePath(subtextConfig));
              setIsSidebarOpen(false);
            }}
          />
        </List>

        {/* Account Section */}
        <Box sx={{ mt: 'auto' }}>
          <Divider />
          <Box sx={{ p: 2 }}>
            {user ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Typography variant='body2' sx={{ color: '#666', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {user.email}
                </Typography>
                <button
                  onClick={async () => {
                    await signOut();
                    setIsSidebarOpen(false);
                  }}
                  style={{
                    background: 'none',
                    border: '1px solid #ddd',
                    borderRadius: '6px',
                    padding: '8px 16px',
                    cursor: 'pointer',
                    fontSize: '0.875rem',
                    color: '#666',
                    width: '100%',
                  }}
                >
                  Sign Out
                </button>
              </Box>
            ) : (
              <button
                onClick={() => {
                  openAuthModal();
                  setIsSidebarOpen(false);
                }}
                style={{
                  width: '100%',
                  background: '#333',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  padding: '10px 16px',
                  cursor: 'pointer',
                  fontSize: '0.9rem',
                  fontWeight: 600,
                }}
              >
                Sign In
              </button>
            )}
          </Box>

          {/* Footer Links */}
          <Divider />
          <Box sx={{ py: 1 }}>
            <Box sx={{ display: 'flex', flexDirection: 'row', gap: 4, alignItems: 'center', justifyContent: 'center' }}>
              <Box
                component="a"
                href="https://reddit.com/r/WeaverGames"
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  background: 'none',
                  border: 'none',
                  padding: '4px 0',
                  cursor: 'pointer',
                  fontSize: '0.875rem',
                  color: '#666',
                  textDecoration: 'underline',
                  textAlign: 'left',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  '&:hover': {
                    color: '#000'
                  }
                }}
              >
                <RedditIcon />
                r/WeaverGames
              </Box>
              <Box
                component="button"
                onClick={() => {
                                  navigate('/privacy');
                  setIsSidebarOpen(false);
                }}
                sx={{
                  background: 'none',
                  border: 'none',
                  padding: '4px 0',
                  cursor: 'pointer',
                  fontSize: '0.875rem',
                  color: '#666',
                  textDecoration: 'underline',
                  textAlign: 'left',
                  '&:hover': {
                    color: '#000'
                  }
                }}
              >
                Privacy Policy
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
    </Drawer>
  );
}
